// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.

import { AddCashModal } from '@advisor-app/components';
import { capitalizeString } from '@shared/ui-kit/utils/capitalize-string';
import type { Paths } from '@shared/data-access/interfaces/routes/paths';
import { Button } from '@mui/material';
import { Heading, SectionContainer } from '@shared/ui-kit/components';
import { type FC, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useAnalyzeActions } from '../hooks/use-analyze-actions';
import { type PortfolioMarketPlaceResponseBody as PortfolioDetails } from '@shared/data-access/interfaces/api/engine/portfolio-types';

export interface MarketplaceItemProps {
  portfolio: PortfolioDetails;
}

export const MarketplaceItem: FC<MarketplaceItemProps> = ({ portfolio }) => {
  const { t } = useTranslation();
  const { handleAnalyze, handleClose, open, dataPortfolio, workflowAsset } =
    useAnalyzeActions(portfolio);

  const properties = useMemo(
    () => [
      {
        label: t('glossary.assets-manager', { ns: 'glossary' }),
        value: portfolio.asset_manager,
      },
      {
        label: t('marketplace.client-residency', { ns: 'components' }),
        value: portfolio.client_residency,
      },
      { label: t('esg', { ns: 'components' }), value: portfolio.esg },
      {
        label: t('common.description', { ns: 'common' }),
        value: portfolio.description,
      },
    ],
    [portfolio, t],
  );

  return (
    <SectionContainer>
      <Heading
        variant="h2"
        className="pb-8 !text-xl"
        title={capitalizeString(portfolio.portfolio)}
      />

      <div className="text-graph-text">
        <dl className="flex flex-col space-y-6 [&_dt]:font-semibold">
          {properties.map(property => (
            <div
              className={
                property.label === 'Description'
                  ? 'text-justify'
                  : 'inline-flex space-x-2'
              }
              key={property.value}
            >
              <dt>{property.label}:</dt>
              <dd>{property.value}</dd>
            </div>
          ))}
        </dl>
      </div>

      <div className="mt-16 flex justify-center">
        <Button color="secondary" onClick={handleAnalyze}>
          {t('common.analyze', { ns: 'common' })}
        </Button>
      </div>

      <AddCashModal
        openModal={open}
        handleClose={handleClose}
        dataPortfolio={dataPortfolio}
        to={`/analysis/${workflowAsset}` as Paths}
      />
    </SectionContainer>
  );
};
